<template>
  <div v-if="show" class="modal-mask" @click="handleBackdropClick">
    <div class="modal-dialog modal-dialog-centered" @click.stop>
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{
              targetType === CDR_SCOPE_TYPE.PATIENT
                ? lang.get("admin.cdr.reportPatient")
                : lang.get("admin.cdr.reportCase")
            }}
          </h5>
          <button type="button" class="close" @click="close">
            <span>&times;</span>
          </button>
        </div>

        <form @submit.prevent="handleSubmit">
          <div class="modal-body">
            <div v-if="configLoading" class="text-center py-3">
              <span class="fas fa-spinner fa-spin"></span>
            </div>

            <template v-else>
              <div class="form-group">
                <label
                  >{{ lang.get("admin.cdr.reasonCategory") }}
                  <span class="text-danger">*</span></label
                >
                <select v-model="form.reason_category" class="form-control">
                  <option value="">
                    {{ lang.get("general.pleaseSelect") }}
                  </option>
                  <option
                    v-for="reason in config"
                    :key="reason.value"
                    :value="reason.value"
                  >
                    {{ lang.get("admin.cdr.reason." + reason.value) }}
                  </option>
                </select>
                <div
                  v-if="errors.reason_category"
                  class="invalid-feedback d-block"
                >
                  {{ errors.reason_category[0] }}
                </div>
              </div>

              <p v-if="descriptionText" class="text-muted small">
                {{ descriptionText }}
              </p>

              <div v-if="requiresReasonText" class="form-group">
                <label
                  >{{ lang.get("admin.cdr.reasonText") }}
                  <span class="text-danger">*</span></label
                >
                <textarea
                  v-model="form.reason_text"
                  class="form-control"
                  rows="3"
                  :placeholder="lang.get('admin.cdr.reasonTextPlaceholder')"
                />
                <div v-if="errors.reason_text" class="invalid-feedback d-block">
                  {{ errors.reason_text[0] }}
                </div>
              </div>

              <div v-if="showActionSelect" class="form-group">
                <label
                  >{{ lang.get("admin.cdr.requestedAction") }}
                  <span class="text-danger">*</span></label
                >
                <select v-model="form.requested_action" class="form-control">
                  <option value="">
                    {{ lang.get("general.pleaseSelect") }}
                  </option>
                  <option
                    v-for="action in selectedReasonConfig?.available_actions"
                    :key="action"
                    :value="action"
                  >
                    {{ lang.get("admin.cdr.action." + action) }}
                  </option>
                </select>
                <div
                  v-if="errors.requested_action"
                  class="invalid-feedback d-block"
                >
                  {{ errors.requested_action[0] }}
                </div>
              </div>

              <div v-if="consequenceText" class="alert alert-warning">
                {{ consequenceText }}
              </div>

              <div
                v-if="
                  form.requested_action === CDR_REQUESTED_ACTION.REASSIGNMENT
                "
                class="form-group"
              >
                <label
                  >{{ lang.get("admin.cdr.destinationPatientNote") }}
                  <span class="text-danger">*</span></label
                >
                <textarea
                  v-model="form.destination_patient_note"
                  class="form-control"
                  rows="3"
                  :placeholder="
                    lang.get('admin.cdr.destinationPatientNotePlaceholder')
                  "
                />
                <div
                  v-if="errors.destination_patient_note"
                  class="invalid-feedback d-block"
                >
                  {{ errors.destination_patient_note[0] }}
                </div>
              </div>

              <div
                v-if="
                  woundAcknowledgementRequired && form.requested_action !== ''
                "
                class="form-group"
              >
                <div class="alert alert-warning">
                  <div class="form-check">
                    <input
                      id="wound-cascade-ack"
                      v-model="woundAcknowledged"
                      type="checkbox"
                      class="form-check-input"
                    />
                    <label class="form-check-label" for="wound-cascade-ack">
                      {{ woundAcknowledgementText }}
                    </label>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div v-if="submitError" class="alert alert-danger mx-3 mb-0">
            {{ submitError }}
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="close">
              {{ lang.get("general.modalCancelButton") }}
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="!isFormValid || submitting || configLoading"
            >
              {{ lang.get("admin.cdr.reportSubmit") }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from "vue";
import { practitionerHttpService } from "../../app/practitioner-portal/store/practitioner-http-service";
import { lang } from "../../i18n";
import { triggerPatientCdrRefresh } from "./cdr-patient-refresh";
import type { WoundAcknowledgement } from "./wound-acknowledgement";
import {
  CDR_REQUESTED_ACTION,
  CDR_SCOPE_TYPE,
  type CdrFlaggableScopeType,
} from "./cdr-constants";

interface ReasonConfig {
  value: string;
  auto_action: string | null;
  available_actions: string[];
  requires_reason_text: boolean;
}

const props = defineProps<{
  show: boolean;
  targetType: CdrFlaggableScopeType;
  targetId: number;
  woundAcknowledgement?: WoundAcknowledgement;
  submitUrl?: string;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "created"): void;
}>();

const config = ref<ReasonConfig[]>([]);
const configLoading = ref(false);
const form = ref({
  reason_category: "",
  reason_text: "",
  requested_action: "",
  destination_patient_note: "",
});
const errors = ref<Record<string, string[]>>({});
const submitError = ref<string | null>(null);
const submitting = ref(false);
const woundAcknowledged = ref(false);

const selectedReasonConfig = computed(() =>
  config.value.find((r) => r.value === form.value.reason_category),
);

// Auto-set action when only one valid option; clear when reason changes.
watch(
  () => form.value.reason_category,
  () => {
    const reasonConfig = selectedReasonConfig.value;
    form.value.requested_action = reasonConfig?.auto_action ?? "";
    form.value.reason_text = "";
  },
);

const showActionSelect = computed(
  () =>
    selectedReasonConfig.value !== undefined &&
    selectedReasonConfig.value.auto_action === null,
);

const requiresReasonText = computed(
  () => selectedReasonConfig.value?.requires_reason_text === true,
);

const descriptionText = computed((): string => {
  if (!form.value.reason_category) return "";
  return (
    lang.get("admin.cdr.reasonDescription." + form.value.reason_category) ?? ""
  );
});

const consequenceText = computed((): string => {
  const reason = form.value.reason_category;
  if (!reason) return "";
  const reasonConfig = selectedReasonConfig.value;
  if (!reasonConfig) return "";
  if (reasonConfig.auto_action === null) {
    if (!form.value.requested_action) return "";
    return (
      lang.get("admin.cdr.actionConsequence." + form.value.requested_action) ??
      ""
    );
  }
  return lang.get("admin.cdr.reasonConsequence." + reason) ?? "";
});

const woundAcknowledgementRequired = computed((): boolean => {
  if (!props.woundAcknowledgement) return false;
  const { isMainExamination, subExaminationCount } = props.woundAcknowledgement;
  if (isMainExamination && subExaminationCount > 0) return true;
  if (!isMainExamination && subExaminationCount > 1) return true;
  return false;
});

const woundAcknowledgementKey = computed((): string => {
  if (!props.woundAcknowledgement) return "";
  return props.woundAcknowledgement.isMainExamination
    ? "admin.cdr.woundMainAcknowledgement"
    : "admin.cdr.woundSubAcknowledgement";
});

const woundAcknowledgementText = computed((): string => {
  if (!props.woundAcknowledgement) return "";
  if (props.woundAcknowledgement.isMainExamination) {
    return (
      lang.choice(
        "admin.cdr.woundMainAcknowledgement",
        props.woundAcknowledgement.subExaminationCount,
      ) ?? ""
    );
  }
  return lang.get("admin.cdr.woundSubAcknowledgement") ?? "";
});

const isSubExamination = computed((): boolean => {
  if (!props.woundAcknowledgement) return false;
  return !props.woundAcknowledgement.isMainExamination;
});

const isFormValid = computed((): boolean => {
  if (!form.value.reason_category || !form.value.requested_action) return false;
  if (requiresReasonText.value && !form.value.reason_text.trim()) return false;
  if (
    form.value.requested_action === CDR_REQUESTED_ACTION.REASSIGNMENT &&
    !form.value.destination_patient_note.trim()
  )
    return false;
  if (
    woundAcknowledgementRequired.value &&
    form.value.requested_action !== "" &&
    !woundAcknowledged.value
  )
    return false;
  return true;
});

watch(
  () => props.show,
  async (isVisible) => {
    if (isVisible) {
      resetForm();
      document.body.style.overflow = "hidden";
      await fetchConfig();
    } else {
      document.body.style.overflow = "";
    }
  },
  { immediate: true },
);

// Belt-and-braces cleanup: when the parent uses `v-if` to mount/unmount this
// dialog, the watcher above can be torn down before its `false` branch runs
// (e.g. on the close-after-submit flow), leaving body { overflow: hidden }
// stuck and locking the page until a full reload.
onUnmounted(() => {
  if (document.body.style.overflow === "hidden") {
    document.body.style.overflow = "";
  }
});

async function fetchConfig() {
  configLoading.value = true;
  try {
    let url = "/api/internal/cdr-flag/config?target_type=" + props.targetType;
    if (isSubExamination.value) {
      url += "&is_sub_examination=1";
    }
    const response = await practitionerHttpService.get(url);
    config.value = response.data.reasons;
  } catch {
    submitError.value =
      lang.get("admin.cdr.submitError") ??
      "An error occurred. Please try again.";
  } finally {
    configLoading.value = false;
  }
}

function resetForm() {
  form.value = {
    reason_category: "",
    reason_text: "",
    requested_action: "",
    destination_patient_note: "",
  };
  errors.value = {};
  submitError.value = null;
  woundAcknowledged.value = false;
  config.value = [];
}

function close() {
  emit("close");
}

function handleBackdropClick(event: Event) {
  if (event.target === event.currentTarget) close();
}

async function handleSubmit() {
  submitting.value = true;
  errors.value = {};
  submitError.value = null;

  const endpoint =
    props.submitUrl ??
    (props.targetType === CDR_SCOPE_TYPE.EXAMINATION
      ? "/api/internal/examinations/" + props.targetId + "/cdr-flag"
      : "/api/internal/patients/" + props.targetId + "/cdr-flag");

  const payload: Record<string, unknown> = {
    reason_category: form.value.reason_category,
    reason_text: requiresReasonText.value ? form.value.reason_text : null,
    requested_action: form.value.requested_action,
    destination_patient_note: form.value.destination_patient_note || null,
  };

  if (woundAcknowledgementRequired.value && woundAcknowledged.value) {
    payload.wound_cascade_acknowledgement = woundAcknowledgementKey.value;
    payload.wound_cascade_sub_count =
      props.woundAcknowledgement?.subExaminationCount ?? null;
  }

  try {
    await practitionerHttpService.post(endpoint, payload);
    triggerPatientCdrRefresh();
    emit("created");
    close();
  } catch (error: any) {
    if (error.response?.status === 422) {
      errors.value = error.response.data;
    } else {
      submitError.value =
        lang.get("admin.cdr.submitError") ??
        "An error occurred. Please try again.";
    }
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
.modal-mask {
  text-align: left;
  white-space: normal;
}
</style>
